import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";

import { AlertTriangle, Trash2 } from "lucide-react";
import { useState } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import { UUID } from "@goose/utils/uuid";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";
import { RequirementSetIcon } from "~/components/admin/Icons";
import { PageHeader } from "~/components/admin/PageHeader";
import { Surface } from "~/components/admin/Surface";
import { QueryState } from "~/components/QueryState";
import { RequirementCards } from "~/components/RequirementCards";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { AlertDialog } from "~/components/ui/AlertDialog";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId",
)({
  component: RouteComponent,
  params: {
    parse: ({ requirementsId }) => {
      return {
        requirementsId: UUID.from(requirementsId),
      };
    },
    stringify: ({ requirementsId }) => {
      return {
        requirementsId: requirementsId.toShort(),
      };
    },
  },
  beforeLoad: ({ params, location }) => {
    // Canonicalize long-form UUID URLs to the Base58 short form.
    if (!location.pathname.endsWith(params.requirementsId.toShort())) {
      throw redirect({
        to: "/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId",
        params,
        replace: true,
      });
    }
  },
});

function RouteComponent() {
  const trpc = useTRPC();
  const { requirementsId } = Route.useParams();

  const requirementSet = useQuery(
    trpc.opportunities.requirements.get.queryOptions({
      id: requirementsId.toString(),
    }),
  );

  return (
    <QueryState
      result={requirementSet}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => (
        <div className="mt-6 space-y-6">
          <RequirementSetDetails requirementSet={data} />

          <Surface as="section" className="p-6">
            <RequirementCards requirements={data.requirements} showDebug />
          </Surface>
        </div>
      )}
    </QueryState>
  );
}

interface RequirementSetDetailsProps {
  requirementSet: InferTransformedProcedureOutput<"opportunities.requirements.get">;
}

function RequirementSetDetails({ requirementSet }: RequirementSetDetailsProps) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const onDelete = async () => {
    // Invalidate the requirements list query to refresh the table
    await queryClient.invalidateQueries({
      queryKey: trpc.opportunities.requirements.list.queryKey({
        organizationId: requirementSet.organizationId,
        opportunityId: requirementSet.opportunityId,
      }),
    });

    await navigate({
      to: "/admin/organizations/$organizationId/opportunities/$opportunityId/requirements",
      params: {
        organizationId: requirementSet.organizationId,
        opportunityId: requirementSet.opportunityId,
      },
    });
  };

  return (
    <>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Link
              to="/admin/organizations/$organizationId/opportunities/$opportunityId/requirements"
              params={{
                organizationId: requirementSet.organizationId,
                opportunityId: requirementSet.opportunityId,
              }}
            >
              Requirement sets
            </Link>
            <span>{UUID.fromString(requirementSet.id).toShort()}</span>
          </Breadcrumbs>
        }
        icon={RequirementSetIcon}
        title="Requirement set"
        actions={
          <>
            <RequirementSetDeleteButton
              requirementSetId={requirementSet.id}
              onDelete={() => {
                void onDelete();
              }}
            />
          </>
        }
      />
      <DetailsTable
        details={[
          {
            label: "ID",
            value: UUID.fromString(requirementSet.id).toShort(),
          },
          {
            label: "Created by",
            value: (
              <Link
                to="/admin/organizations/$organizationId/users/$userId"
                className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                params={{
                  organizationId: requirementSet.organizationId,
                  userId: requirementSet.createdBy.id,
                }}
              >
                {requirementSet.createdBy.name}
              </Link>
            ),
          },
          requirementSet.modification && {
            label: "Modification",
            value: requirementSet.modification,
          },
          requirementSet.parentId && {
            label: "Parent",
            value: (
              <Link
                to="/admin/organizations/$organizationId/opportunities/$opportunityId/requirements/$requirementsId"
                className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                params={{
                  organizationId: requirementSet.organizationId,
                  opportunityId: requirementSet.opportunityId,
                  requirementsId: UUID.fromString(requirementSet.parentId),
                }}
              >
                {UUID.fromString(requirementSet.parentId).toShort()}
              </Link>
            ),
          },
          {
            label: "Model usage",
            value: `${requirementSet.modelUsage.model}  (${requirementSet.modelUsage.inputTokenCount} in / ${requirementSet.modelUsage.outputTokenCount} out)`,
          },
          requirementSet.sourceHash && {
            label: "Source hash",
            value: requirementSet.sourceHash,
          },
          {
            label: "Created at",
            value: formatDate(requirementSet.createdAt),
          },
        ]}
      />
    </>
  );
}

interface RequirementSetDeleteButtonProps {
  requirementSetId: string;
  onDelete: () => void;
}

function RequirementSetDeleteButton({
  requirementSetId,
  onDelete,
}: RequirementSetDeleteButtonProps) {
  const [open, setOpen] = useState(false);
  const trpc = useTRPC();

  const deleteRequirementSet = useMutation(
    trpc.opportunities.requirements.remove.mutationOptions(),
  );

  const handleDelete = async () => {
    await deleteRequirementSet.mutateAsync({
      id: requirementSetId,
    });
    onDelete();
    setOpen(false);
  };

  return (
    <>
      <AlertDialog
        open={open}
        onOpenChange={(open) => {
          if (!open && !deleteRequirementSet.isPending) {
            setOpen(false);
          }
        }}
      >
        <Button asChild variant="destructive" size="sm">
          <AlertDialog.Trigger
            onClick={() => {
              setOpen(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </AlertDialog.Trigger>
        </Button>
        <AlertDialog.Content className="max-w-md">
          <AlertDialog.Title>Delete requirement set</AlertDialog.Title>
          <AlertDialog.Description>
            This permanently removes the requirement snapshot and its references
            from the current opportunity workspace. Are you sure you want to
            delete this requirement set?
          </AlertDialog.Description>

          {deleteRequirementSet.isError ? (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <AlertTriangle className="mr-2 h-4 w-4" />
              {deleteRequirementSet.error.message}
            </div>
          ) : null}

          <AlertDialog.Footer>
            <AlertDialog.Close disabled={deleteRequirementSet.isPending}>
              Cancel
            </AlertDialog.Close>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                void handleDelete();
              }}
              disabled={deleteRequirementSet.isPending}
            >
              {deleteRequirementSet.isPending ? "Deleting..." : "Delete"}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  );
}
